/**
 * SEORouter — resolves per-route metadata by pathname and renders <SEO/>.
 *
 * Handles:
 *  • static routes from PAGE_META
 *  • dynamic /products/:slug via CATEGORIES.seo overrides
 *  • unknown paths → default site metadata
 *
 * Category and page-level schema (BreadcrumbList, Product, FAQPage) is
 * emitted inside the individual page component so it stays close to the
 * data it describes.
 */

import { useLocation } from "react-router-dom";
import { SEO } from "./SEO";
import { PAGE_META } from "@/lib/seo/pages";
import { CATEGORIES } from "@/data/productCategories";

const CATEGORY_PREFIX = "/products/";

export const SEORouter = () => {
  const { pathname } = useLocation();

  // Category detail pages — /products/steel-doors etc.
  if (
    pathname.startsWith(CATEGORY_PREFIX) &&
    pathname !== "/products/designs" &&
    !pathname.startsWith("/products/designs/")
  ) {
    const slug = pathname.replace(CATEGORY_PREFIX, "").split("/")[0];
    const cat = CATEGORIES[slug];
    if (cat) {
      const seo = cat.seo || {};
      return (
        <SEO
          path={pathname}
          title={seo.title || `${cat.title.replace(/\.$/, "")} — EUROSTEEL NZ`}
          description={seo.description || cat.description}
          keywords={seo.keywords}
          ogImage={seo.ogImage || cat.heroImage}
          ogType="product"
        />
      );
    }
  }

  // Design-detail pages — noindex until Phase 2 spec pages ship.
  if (pathname.startsWith("/products/designs/")) {
    return (
      <SEO
        path={pathname}
        title="Design Detail | EUROSTEEL"
        description="Detail view of a bespoke EUROSTEEL steel design render."
        noindex={false}
      />
    );
  }

  const meta = PAGE_META[pathname] || PAGE_META["/"];
  return (
    <SEO
      path={pathname}
      title={meta.title}
      description={meta.description}
      keywords={meta.keywords}
      ogImage={meta.ogImage}
      ogType={meta.ogType}
      noindex={meta.noindex}
    />
  );
};

export default SEORouter;
